import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuLabel, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, FileText, Users, MoreHorizontal, ExternalLink, ClipboardCopy,
  CheckCircle2, Eye, Send, ShieldCheck, ChevronDown, GitCompare, Star, KeyRound,
  UserPlus, Trash2, BarChart3, Download, Plus, FolderOpen,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { DocumentPanel } from '@/components/shared/DocumentPanel';
import { recruitmentAdminApi, recruitmentClientApi } from '@/lib/recruitmentApi';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { bizDate } from '@/lib/dates';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import CandidateComparisonSheet from '@/components/recruitment/CandidateComparisonSheet';
import RosterFilterBar, {
  EMPTY_ROSTER_FILTERS, applyRosterFilters,
} from '@/components/recruitment/RosterFilterBar';
import { DirectResultsDisclaimer } from '@/components/shared/DirectResultsDisclaimer';
import ConvertToCollaboratorDialog from '@/components/recruitment/ConvertToCollaboratorDialog';
import { useApp } from '@/context/AppContext';

/**
 * Sprint A · G-1 — Named candidate roster card.
 *
 * Single component, used in two places via `variant`:
 *  - variant="admin"  → mounted on AdminRecruitmentDetail.jsx
 *  - variant="client" → mounted on ClientRecruitment.jsx (detail view)
 *
 * Reuses existing shadcn primitives (Card, Table, Badge, Button) — zero new
 * design tokens, no new layouts.
 */
function fmtDate(iso, lang) {
  return bizDate(iso, lang);
}

function LabelXs({ children }) {
  return <p className="text-xs font-medium text-muted-foreground mb-0.5">{children}</p>;
}

function ScoreCell({ value, band, hidden, hiddenLabel }) {
  if (hidden) {
    return <span className="text-xs italic text-muted-foreground">{hiddenLabel}</span>;
  }
  if (value == null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="tabular-nums font-semibold">{value}</span>
      {band && <Badge variant="outline" className="text-xs">{band}</Badge>}
    </span>
  );
}

function ReportStatusBadge({ status, t, isClient }) {
  if (!status || status === 'none') {
    return <span className="text-xs text-muted-foreground">{t('recruitment.candidates.reportStatus.none')}</span>;
  }
  // Split model — the client sees the protected narrative deliverable as a
  // single "quality review" state until it is released. Never presented as
  // approval of the client's process.
  if (isClient && status !== 'released_to_client') {
    return (
      <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-xs">
        {t('recruitment.candidates.interpretiveQA', { defaultValue: 'Informe interpretativo — control de calidad CTH' })}
      </Badge>
    );
  }
  const tone =
    status === 'released_to_client' ? 'bg-success/10 text-success border-success/20'
    : status === 'approved'         ? 'bg-info/10 text-info border-info/20'
    : status === 'rejected'         ? 'bg-destructive/10 text-destructive border-destructive/20'
    : 'bg-muted text-muted-foreground border-border';
  return (
    <Badge variant="outline" className={`${tone} text-xs`}>
      {t(`recruitment.candidates.reportStatus.${status}`, { defaultValue: status })}
    </Badge>
  );
}

export function CandidateRosterCard({ processId, variant = 'admin', canManage = false, canActivate = false, onProcessChanged, companyId = null }) {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Client Autonomy P0-1 — single candidate-management experience (client).
  const [addOpen, setAddOpen] = useState(false);
  const [cand, setCand] = useState({ name: '', email: '', phone: '', cv_url: '' });
  const [addBusy, setAddBusy] = useState(false);
  const [lib, setLib] = useState([]);
  const [selPool, setSelPool] = useState([]); // pool_candidate_ids
  const [selQs, setSelQs] = useState([]); // questionnaire ids (battery-capable)
  const [assignBusy, setAssignBusy] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(null);
  const [docsRow, setDocsRow] = useState(null); // V5.0 §9.2 — candidate documents

  // V5.4 §A — secure CV invitation link (copy + optional email)
  const inviteCandidate = async (c) => {
    try {
      const { data } = await api.post(
        `/client/recruitment-processes/${processId}/candidates/${c.pool_candidate_id}/invite`);
      const link = `${window.location.origin}${data.link_path}`;
      try { await navigator.clipboard.writeText(link); } catch { /* ignore */ }
      toast.success(
        L('Enlace de invitación copiado (vence ', 'Invitation link copied (expires ')
        + String(data.expires_at).slice(0, 10) + ')',
        { description: link, duration: 12000,
          action: { label: L('Enviar por email', 'Send by email'),
                    onClick: async () => {
                      try {
                        const r = await api.post(`/client/recruitment-processes/${processId}/candidates/${c.pool_candidate_id}/invite/send`);
                        toast.success(L('Invitación enviada a ', 'Invitation sent to ') + r.data.sent_to);
                      } catch (e) { toast.error(formatApiError(e)); }
                    } } });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // Founder 2026-06 P0 — add additional assessments to an EXISTING candidate.
  const [addAssessFor, setAddAssessFor] = useState(null); // roster row
  const [addAssessSel, setAddAssessSel] = useState([]);
  const [addAssessBusy, setAddAssessBusy] = useState(false);
  // Sprint TC-A.2 — inline row actions state.
  const [runningKey, setRunningKey] = useState(null); // `${aid}:${action}` while in flight
  const [releaseConfirm, setReleaseConfirm] = useState(null); // {report_id, candidate_name}
  // Sprint TC-A.3 — per-candidate decisions.
  const [decisionBusy, setDecisionBusy] = useState(null); // candidate_user_id while saving
  const [flashRowId, setFlashRowId] = useState(null);     // 1-s post-update flash highlight
  // Sprint TC-C.1 — selection for side-by-side comparison (admin only).
  const [selectedAids, setSelectedAids] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  // Sprint TC-C.2 — admin-only filter state + per-row shortlist busy flag.
  //   TC-D.1 polish — persist filter state per (user, process) in
  //   localStorage so an advisor who refreshes mid-triage doesn't lose
  //   their filters. Pure FE polish — no backend preference system.
  const { user: currentUser } = useApp();
  const filterStorageKey = (variant !== 'client' && currentUser?.id && processId)
    ? `tc:rosterFilters:${currentUser.id}:${processId}`
    : null;
  const [filters, setFilters] = useState(() => {
    if (!filterStorageKey) return EMPTY_ROSTER_FILTERS;
    try {
      const raw = localStorage.getItem(filterStorageKey);
      if (!raw) return EMPTY_ROSTER_FILTERS;
      const parsed = JSON.parse(raw);
      return { ...EMPTY_ROSTER_FILTERS, ...(parsed || {}) };
    } catch {
      return EMPTY_ROSTER_FILTERS;
    }
  });
  useEffect(() => {
    if (!filterStorageKey) return;
    try { localStorage.setItem(filterStorageKey, JSON.stringify(filters)); }
    catch { /* private mode / quota — ignore */ }
  }, [filterStorageKey, filters]);
  const [shortlistBusy, setShortlistBusy] = useState(null); // candidate_user_id while saving
  // P0 UAT 2026-08-10 — external-candidate access dialog state.
  const [accessRow, setAccessRow] = useState(null);
  // CTH-REC-075 — explicit client hiring decision per candidate.
  const clientDecision = async (c, status) => {
    const msg = status === 'hired'
      ? L(`¿Confirmar a ${c.candidate_name} como el candidato contratado? Después podrás activarlo como colaborador.`,
          `Confirm ${c.candidate_name} as the hired candidate? You can then activate them as an employee.`)
      : L(`¿Marcar a ${c.candidate_name} como "No contratado"?`, `Mark ${c.candidate_name} as "Not selected"?`);
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/client/recruitment-processes/${processId}/candidates/${c.candidate_user_id}/client-decision`, { status });
      toast.success(status === 'hired'
        ? L('Decisión registrada: contratado. Usa "Activar como colaborador" para incorporarlo.', 'Decision recorded: hired. Use "Activate as Employee" to onboard them.')
        : L('Decisión registrada: no contratado.', 'Decision recorded: not selected.'));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [convertFor, setConvertFor] = useState(null); // Founder UAT 2026-06 — hired → collaborator
  const [resultsRow, setResultsRow] = useState(null);
  const [resultsData, setResultsData] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsDlBusy, setResultsDlBusy] = useState(null);

  const downloadResults = async (fmt) => {
    if (!resultsRow) return;
    setResultsDlBusy(fmt);
    try {
      const r = await api.get(
        `/client/recruitment-processes/${processId}/candidates/${resultsRow.assignment_id}/results/export/${fmt}`,
        { responseType: 'blob', timeout: 60000 },
      );
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      const cand = (resultsData?.candidate_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      a.download = `${isES ? 'resultados-automaticos' : 'automatic-results'}${cand ? `-${cand}` : ''}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResultsDlBusy(null); }
  };

  const openResults = async (row) => {
    setResultsRow(row); setResultsData(null); setResultsLoading(true);
    try {
      const r = await api.get(`/client/recruitment-processes/${processId}/candidates/${row.assignment_id}/results`);
      setResultsData(r.data);
    } catch (e) { toast.error(formatApiError(e)); setResultsRow(null); }
    finally { setResultsLoading(false); }
  };   // roster row being managed
  const [accessInfo, setAccessInfo] = useState(null); // GET .../access payload
  const [accessResult, setAccessResult] = useState(null); // POST result (temp pwd, once)
  const [accessBusy, setAccessBusy] = useState(false);

  const openAccess = async (row) => {
    setAccessRow(row);
    setAccessInfo(null);
    setAccessResult(null);
    try {
      const r = await api.get(`/admin/recruitment-processes/${processId}/candidates/${row.assignment_id}/access`);
      setAccessInfo(r.data);
    } catch (e) {
      toast.error(t('recruitment.candidates.access.failed'), { description: formatApiError(e) });
    }
  };

  const activateAccess = async () => {
    if (!accessRow) return;
    setAccessBusy(true);
    try {
      const r = await api.post(`/admin/recruitment-processes/${processId}/candidates/${accessRow.assignment_id}/access`);
      setAccessResult(r.data);
      toast.success(t('recruitment.candidates.access.activated'));
      await load();
    } catch (e) {
      toast.error(t('recruitment.candidates.access.failed'), { description: formatApiError(e) });
    } finally {
      setAccessBusy(false);
    }
  };

  const isClient = variant === 'client';
  const fetcher = isClient
    ? recruitmentClientApi.listCandidates
    : recruitmentAdminApi.listCandidates;
  const reportHref = isClient
    ? (rid) => `/client/reports/${rid}`
    : (rid) => `/admin/reports/${rid}`;

  const load = useCallback(async () => {
    if (!processId) return;
    setLoading(true);
    try {
      const data = await fetcher(processId);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(t('recruitment.candidates.loadError'), { description: formatApiError(e) });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [processId, fetcher, t]);

  useEffect(() => { load(); }, [load]);

  // Client Autonomy — approved Library instruments for assignment (client only).
  useEffect(() => {
    if (variant === 'client' && canManage) {
      api.get('/client/library', { params: { suite: 'talentcheck' } })
        .then((r) => setLib(r.data || [])).catch(() => setLib([]));
    }
  }, [variant, canManage]);

  const addCandidate = async () => {
    if (cand.name.trim().length < 2 || !cand.email.includes('@')) {
      toast.error(L('Nombre y correo válidos requeridos.', 'Valid name and email required.'));
      return;
    }
    setAddBusy(true);
    try {
      await api.post(`/admin/recruitment-processes/${processId}/candidates`, {
        name: cand.name.trim(), email: cand.email.trim(),
        phone: cand.phone.trim() || null, source: 'client_referral',
        cv_url: cand.cv_url.trim() || null,
      });
      setCand({ name: '', email: '', phone: '', cv_url: '' });
      setAddOpen(false);
      toast.success(L('Candidato agregado al proceso.', 'Candidate added to the process.'));
      await load();
      onProcessChanged && onProcessChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setAddBusy(false); }
  };

  const assignSelected = async () => {
    if (selQs.length === 0 || selPool.length === 0) return;
    setAssignBusy(true);
    try {
      await api.post(`/admin/recruitment-processes/${processId}/assign-assessments`,
        { candidate_ids: selPool, questionnaire_ids: selQs });
      toast.success(L('Evaluaciones asignadas desde la Biblioteca.', 'Assessments assigned from the Library.'));
      setSelPool([]); setSelQs([]);
      await load();
      onProcessChanged && onProcessChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setAssignBusy(false); }
  };

  const removePoolCandidate = async (row) => {
    setRemoveBusy(row.pool_candidate_id);
    try {
      await api.delete(`/admin/recruitment-processes/${processId}/candidates/${row.pool_candidate_id}`);
      toast.success(L('Candidato retirado del proceso.', 'Candidate removed from the process.'));
      setSelPool((s) => s.filter((x) => x !== row.pool_candidate_id));
      await load();
      onProcessChanged && onProcessChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setRemoveBusy(null); }
  };

  // Sprint TC-C.2 — visible rows (admin variant only; client roster stays raw).
  const visibleRows = React.useMemo(() => {
    if (isClient) return rows;
    return applyRosterFilters(rows, filters);
  }, [rows, filters, isClient]);

  // P0 add-assessment — per candidate: assigned questionnaire ids + the first
  // assignment row (the "+" button renders once per candidate, not per row).
  const addAssessMeta = React.useMemo(() => {
    const assigned = {};
    const firstAid = {};
    rows.forEach((r) => {
      const uid = r.candidate_user_id;
      if (!uid || !r.assignment_id) return;
      if (!assigned[uid]) assigned[uid] = new Set();
      if (r.questionnaire_id) assigned[uid].add(r.questionnaire_id);
      if (!(uid in firstAid)) firstAid[uid] = r.assignment_id;
    });
    return { assigned, firstAid };
  }, [rows]);

  const addAssessEligible = React.useMemo(() => {
    if (!addAssessFor) return [];
    const taken = addAssessMeta.assigned[addAssessFor.candidate_user_id] || new Set();
    return lib.filter((x) => !taken.has(x.id));
  }, [addAssessFor, addAssessMeta, lib]);

  const submitAddAssessments = async () => {
    if (!addAssessFor || addAssessSel.length === 0) return;
    setAddAssessBusy(true);
    try {
      await api.post(`/admin/recruitment-processes/${processId}/assign-assessments`, {
        user_ids: [addAssessFor.candidate_user_id],
        questionnaire_ids: addAssessSel,
      });
      toast.success(L('Evaluación(es) agregada(s) al candidato.', 'Assessment(s) added to the candidate.'));
      setAddAssessFor(null); setAddAssessSel([]);
      await load();
      onProcessChanged && onProcessChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setAddAssessBusy(false); }
  };

  // TC-C.2 / Client Autonomy P0-2 — toggle shortlist. Client Admin owns
  // ordinary preselection for their own tenant (backend tenant-scoped).
  const toggleShortlist = async (row) => {
    if (isClient && !canManage) return;
    if (!row.candidate_user_id) return;
    const next = !row.shortlisted;
    setShortlistBusy(row.candidate_user_id);
    try {
      await recruitmentAdminApi.toggleShortlist(
        processId, row.candidate_user_id, next,
      );
      toast.success(
        next
          ? t('recruitment.candidates.shortlist.added', {
              defaultValue: 'Added to shortlist.',
            })
          : t('recruitment.candidates.shortlist.removed', {
              defaultValue: 'Removed from shortlist.',
            }),
      );
      // Optimistic local patch + reload to keep server truth.
      setRows((prev) =>
        prev.map((r) =>
          r.candidate_user_id === row.candidate_user_id
            ? { ...r, shortlisted: next }
            : r,
        ),
      );
    } catch (e) {
      toast.error(
        t('recruitment.candidates.shortlist.failed', {
          defaultValue: 'Could not update shortlist.',
        }),
        { description: formatApiError(e) },
      );
    } finally {
      setShortlistBusy(null);
    }
  };

  // Sprint TC-A.2 — execute a row action. Reuses existing admin endpoints
  // (start-review, approve, release). "copy_invite_link" is FE-only.
  const runAction = async (row, action) => {
    if (isClient) return;
    const key = `${row.assignment_id}:${action}`;
    setRunningKey(key);
    try {
      if (action === 'copy_invite_link') {
        const origin = window.location.origin;
        const url = `${origin}/me/assignments/${row.assignment_id}`;
        try {
          await navigator.clipboard.writeText(url);
          toast.success(t('recruitment.candidates.actions.copied', { defaultValue: 'Invite link copied.' }));
        } catch {
          // Fallback when clipboard API is unavailable.
          toast.message(url);
        }
        return;
      }
      if (action === 'start_review') {
        await adminApi.startReview(row.report_id);
        toast.success(t('recruitment.candidates.actions.reviewStarted', { defaultValue: 'Review started.' }));
        await load();
        return;
      }
      if (action === 'approve_report') {
        await adminApi.approve(row.report_id);
        toast.success(t('recruitment.candidates.actions.approved', { defaultValue: 'Report approved.' }));
        await load();
        return;
      }
      if (action === 'release_report') {
        // Show confirmation dialog (human-review acknowledgment).
        setReleaseConfirm({ report_id: row.report_id, candidate_name: row.candidate_name });
        return;
      }
    } catch (e) {
      toast.error(
        t('recruitment.candidates.actions.failed', { defaultValue: 'Action failed.' }),
        { description: formatApiError(e) },
      );
    } finally {
      setRunningKey(null);
    }
  };

  const confirmRelease = async () => {
    if (!releaseConfirm) return;
    const rid = releaseConfirm.report_id;
    setRunningKey(`${rid}:release_report`);
    try {
      await adminApi.release(rid, { human_review_confirmed: true });
      toast.success(t('recruitment.candidates.actions.released', { defaultValue: 'Report released to client.' }));
      setReleaseConfirm(null);
      await load();
    } catch (e) {
      toast.error(
        t('recruitment.candidates.actions.failed', { defaultValue: 'Action failed.' }),
        { description: formatApiError(e) },
      );
    } finally {
      setRunningKey(null);
    }
  };

  // Sprint TC-A.3 — set a per-candidate decision and refetch.
  const setDecision = async (candidate_user_id, status, note) => {
    setDecisionBusy(candidate_user_id);
    try {
      await recruitmentAdminApi.setDecision(processId, {
        candidate_user_id,
        status,
        note: (note || '').trim() || undefined,
      });
      toast.success(t('recruitment.candidates.decision.updated', { defaultValue: 'Decision updated.' }));
      await load();
      setFlashRowId(candidate_user_id);
      setTimeout(() => setFlashRowId(null), 1100);
    } catch (e) {
      toast.error(
        t('recruitment.candidates.decision.failed', { defaultValue: 'Could not save decision.' }),
        { description: formatApiError(e) },
      );
    } finally {
      setDecisionBusy(null);
    }
  };

  return (
    <Card
      className="p-5 shadow-card"
      data-testid={isClient ? 'client-candidate-roster' : 'admin-candidate-roster'}
    >
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-primary" />
        <h3 className="font-semibold">{t('recruitment.candidates.title')}</h3>
        <Badge variant="outline" className="ml-auto text-xs tabular-nums">{rows.length}</Badge>
        {isClient && canManage && (
          <Button size="sm" variant="secondary" className="gap-1.5"
                  onClick={() => setAddOpen((v) => !v)} data-testid="roster-add-candidate-toggle">
            <UserPlus className="h-4 w-4" />
            {t('recruitment.candidates.addCandidate', { defaultValue: L('Agregar candidato', 'Add candidate') })}
          </Button>
        )}
      </div>

      {/* Client Autonomy — inline add-candidate form (single canonical list). */}
      {isClient && canManage && addOpen && (
        <div className="mb-3 rounded-md border border-dashed border-border bg-muted/20 p-3 flex flex-wrap items-end gap-2"
             data-testid="roster-add-candidate-form">
          <div><LabelXs>{L('Nombre del candidato', 'Candidate name')}</LabelXs>
            <input className="h-8 w-44 rounded-md border border-input bg-background px-2 text-sm" value={cand.name}
                   onChange={(e) => setCand({ ...cand, name: e.target.value })} data-testid="tc-cand-name" /></div>
          <div><LabelXs>Email</LabelXs>
            <input className="h-8 w-52 rounded-md border border-input bg-background px-2 text-sm" value={cand.email}
                   onChange={(e) => setCand({ ...cand, email: e.target.value })} data-testid="tc-cand-email" /></div>
          <div><LabelXs>{L('Teléfono (opcional)', 'Phone (optional)')}</LabelXs>
            <input className="h-8 w-36 rounded-md border border-input bg-background px-2 text-sm" value={cand.phone}
                   onChange={(e) => setCand({ ...cand, phone: e.target.value })} data-testid="tc-cand-phone" /></div>
          <div><LabelXs>{L('Enlace al CV (opcional)', 'CV link (optional)')}</LabelXs>
            <input className="h-8 w-52 rounded-md border border-input bg-background px-2 text-sm" placeholder="https://…" value={cand.cv_url}
                   onChange={(e) => setCand({ ...cand, cv_url: e.target.value })} data-testid="tc-cand-cv-url" /></div>
          <Button size="sm" onClick={addCandidate} disabled={addBusy} data-testid="tc-cand-add">
            {addBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}
            {L('Agregar', 'Add')}
          </Button>
        </div>
      )}

      {/* Client Autonomy — assign approved Library assessments to selected pool candidates. */}
      {isClient && canManage && rows.some((r) => r.assessment_status === 'not_assigned') && (
        <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md border border-border bg-muted/10 px-3 py-2"
             data-testid="roster-assign-toolbar">
          <span className="text-xs text-muted-foreground w-full sm:w-auto">
            {L('Selecciona candidatos pendientes y una o varias evaluaciones aprobadas de la Biblioteca (batería).',
               'Select pending candidates and one or more approved Library assessments (battery).')}
          </span>
          <div className="min-w-64 max-w-md flex flex-col gap-1" data-testid="tc-assign-q">
            {lib.length === 0 && (
              <span className="text-xs text-muted-foreground italic">
                {L('No hay evaluaciones autorizadas para TalentCheck en la Biblioteca.', 'No assessments authorized for TalentCheck in the Library.')}
              </span>
            )}
            {lib.map((x) => (
              <label key={x.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={selQs.includes(x.id)}
                       onChange={() => setSelQs((s) => s.includes(x.id) ? s.filter((i) => i !== x.id) : [...s, x.id])}
                       data-testid={`tc-assign-q-${x.id}`} />
                <span className="truncate">{x.name}</span>
              </label>
            ))}
          </div>
          <Button size="sm" onClick={assignSelected} disabled={assignBusy || selQs.length === 0 || selPool.length === 0}
                  className="gap-1.5" data-testid="tc-assign-btn">
            {assignBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {L('Asignar a seleccionados', 'Assign to selected')} ({selPool.length}×{selQs.length})
          </Button>
        </div>
      )}

      {/* Founder-mandated disclaimer whenever DIRECT results are visible. */}
      {isClient && rows.some((r) => r.results_available && !r.report_released) && (
        <DirectResultsDisclaimer className="mb-3" />
      )}

      {/* Sprint TC-C.2 — filter bar (admin only). */}
      {!isClient && rows.length > 0 && (
        <RosterFilterBar
          filters={filters}
          onChange={setFilters}
          rowCount={rows.length}
          visibleCount={visibleRows.length}
        />
      )}

      {/* Sprint TC-C.1 — selection toolbar (admin only) */}
      {!isClient && rows.length >= 2 && (
        <div
          className="mb-3 flex items-center gap-2 flex-wrap rounded-md border border-dashed border-border px-3 py-2 bg-muted/20"
          data-testid="compare-toolbar"
        >
          <span className="text-xs text-muted-foreground">
            {t('recruitment.candidates.compare.selectionHint', {
              defaultValue: 'Select 2-4 candidates to compare side by side.',
            })}
          </span>
          <span className="text-xs font-medium ml-1" data-testid="compare-selection-count">
            {t('recruitment.candidates.compare.selected', {
              defaultValue: '{{n}} selected',
              n: selectedAids.length,
            })}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {selectedAids.length > 0 && (
              <Button
                size="sm" variant="ghost"
                onClick={() => setSelectedAids([])}
                data-testid="compare-clear-selection"
              >
                {t('recruitment.candidates.compare.clear', { defaultValue: 'Clear' })}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setCompareOpen(true)}
              disabled={selectedAids.length < 2 || selectedAids.length > 4}
              className="gap-1.5"
              data-testid="compare-open-btn"
            >
              <GitCompare className="h-4 w-4" />
              {t('recruitment.candidates.compare.cta', { defaultValue: 'Compare' })}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('common.loading')}
        </div>
      ) : rows.length === 0 ? (
        <p
          className="py-6 text-sm text-muted-foreground text-center"
          data-testid={isClient ? 'client-roster-empty' : 'admin-roster-empty'}
        >
          {t('recruitment.candidates.empty')}
        </p>
      ) : (!isClient && visibleRows.length === 0) ? (
        <p
          className="py-6 text-sm text-muted-foreground text-center"
          data-testid="admin-roster-no-matches"
        >
          {t('recruitment.candidates.noMatches', {
            defaultValue: 'No candidates match the current filters.',
          })}
        </p>
      ) : (
        <>
        {/* Founder UAT 2026-10 — hiring result & onboarding section */}
        {(rows.some((r) => r.final_status === 'hired') || rows.some((r) => r.final_status === 'pending_activation')) && (
          <div className="mb-3 space-y-2" data-testid="hiring-result-section">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {L('Resultado de contratación e incorporación', 'Hiring result & onboarding')}
            </p>
            {rows.some((r) => r.final_status === 'hired') ? (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm flex items-center gap-2 flex-wrap"
                   data-testid="roster-hired-banner">
                <Badge className="bg-emerald-600 text-white border-0">{L('Contratado/a', 'Hired')}</Badge>
                <span className="font-medium">
                  {rows.find((r) => r.final_status === 'hired')?.candidate_name}
                </span>
                <span className="text-muted-foreground text-xs">
                  {L('Contratación confirmada', 'Hiring confirmed')}
                  {rows.find((r) => r.final_status === 'hired')?.final_decided_at &&
                    ` · ${String(rows.find((r) => r.final_status === 'hired')?.final_decided_at).slice(0, 10)}`}
                </span>
                {isClient && rows.find((r) => r.final_status === 'hired')?.hired_activated && (
                  <Button size="sm" variant="outline" className="ml-auto gap-1.5 border-emerald-400 text-emerald-700 hover:bg-emerald-100"
                          data-testid="hired-view-employee"
                          onClick={() => { window.location.href = `/client/employees?q=${encodeURIComponent(rows.find((r) => r.final_status === 'hired')?.candidate_name || '')}`; }}>
                    <UserPlus className="h-3.5 w-3.5" />
                    {L('Ver ficha del colaborador', 'View employee profile')}
                  </Button>
                )}
                {isClient && !rows.find((r) => r.final_status === 'hired')?.hired_activated && (
                  <Button size="sm" variant="outline" className="ml-auto gap-1.5 border-amber-400 text-amber-700 hover:bg-amber-100"
                          data-testid="hired-activate-banner-btn"
                          onClick={() => setConvertFor(rows.find((r) => r.final_status === 'hired'))}>
                    <UserPlus className="h-3.5 w-3.5" />
                    {L('Activar como colaborador', 'Activate as Employee')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm space-y-1"
                   data-testid="pending-activation-banner">
                <p className="font-medium text-amber-900">
                  {L('Proceso cerrado como "Seleccionado" — candidato seleccionado aún no identificado.',
                     'Process closed as "Selected" — selected candidate not yet identified.')}
                </p>
                <p className="text-xs text-amber-800">
                  {L('Confirma la decisión por candidato: usa "Confirmar contratación" en la fila del candidato elegido (y "No contratado" en los demás). Después podrás activarlo como colaborador.',
                     'Confirm the decision per candidate: use "Confirm hire" on the chosen candidate\'s row (and "Not selected" on the others). You can then activate them as an employee.')}
                </p>
              </div>
            )}
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              {isClient && canManage && (
                <TableHead className="w-9 px-2">
                  <span className="sr-only">{t('recruitment.candidates.addCandidate', { defaultValue: 'Select' })}</span>
                </TableHead>
              )}
              {isClient && canManage && (
                <TableHead className="w-9 px-2">
                  <span className="sr-only">{t('recruitment.candidates.shortlist.title', { defaultValue: 'Shortlist' })}</span>
                </TableHead>
              )}
              {!isClient && (
                <TableHead className="w-9 px-2">
                  <span className="sr-only">{t('recruitment.candidates.compare.cta', { defaultValue: 'Compare' })}</span>
                </TableHead>
              )}
              {!isClient && (
                <TableHead className="w-9 px-2">
                  <span className="sr-only">{t('recruitment.candidates.shortlist.title', { defaultValue: 'Shortlist' })}</span>
                </TableHead>
              )}
              <TableHead>{t('recruitment.candidates.thCandidate')}</TableHead>
              <TableHead className="hidden md:table-cell">{t('recruitment.candidates.thPosition')}</TableHead>
              {/* Founder UAT 2026-06 — instrument identity + separate status. */}
              <TableHead>{t('recruitment.candidates.thAssessment')}</TableHead>
              <TableHead>{t('recruitment.candidates.thStatus', { defaultValue: L('Estado', 'Status') })}</TableHead>
              <TableHead>{t('recruitment.candidates.thReport')}</TableHead>
              <TableHead className="hidden lg:table-cell">{t('recruitment.candidates.thScore')}</TableHead>
              <TableHead className="hidden xl:table-cell">{t('recruitment.candidates.thLastActivity')}</TableHead>
              {!isClient && (
                <TableHead>{t('recruitment.candidates.thDecision', { defaultValue: 'Decision' })}</TableHead>
              )}
              <TableHead className="text-right">{t('recruitment.candidates.thAction')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(isClient ? rows : visibleRows).map((c) => {
              const rowKey = c.assignment_id || `pool-${c.pool_candidate_id}`;
              const isPoolRow = !c.assignment_id;
              const tid = isClient
                ? `client-roster-row-${rowKey}`
                : `admin-roster-row-${rowKey}`;
              const canOpenReport =
                c.report_id && (!isClient || c.report_released);
              return (
                <TableRow
                  key={rowKey}
                  data-testid={tid}
                  className={
                    flashRowId === c.candidate_user_id
                      ? 'bg-primary/10 transition-colors duration-700'
                      : 'transition-colors duration-700'
                  }
                >
                  {/* Client selection checkbox — pool rows only (assignment). */}
                  {isClient && canManage && (
                    <TableCell className="w-9 px-2">
                      {isPoolRow ? (
                        <Checkbox
                          checked={selPool.includes(c.pool_candidate_id)}
                          onCheckedChange={(v) => {
                            setSelPool((prev) => v
                              ? [...prev, c.pool_candidate_id]
                              : prev.filter((x) => x !== c.pool_candidate_id));
                          }}
                          data-testid={`tc-pool-check-${c.pool_candidate_id}`}
                          aria-label={t('recruitment.candidates.addCandidate', { defaultValue: 'Select candidate' })}
                        />
                      ) : null}
                    </TableCell>
                  )}
                  {/* Shortlist star — client-owned preselection (P0-2). */}
                  {isClient && canManage && (
                    <TableCell className="w-9 px-2">
                      {c.candidate_user_id && !isPoolRow ? (
                        <button
                          type="button"
                          onClick={() => toggleShortlist(c)}
                          disabled={shortlistBusy === c.candidate_user_id}
                          className={`p-1 rounded-md transition-colors ${
                            c.shortlisted
                              ? 'text-amber-500 hover:bg-amber-50'
                              : 'text-muted-foreground hover:text-amber-500 hover:bg-amber-50'
                          }`}
                          data-testid={`${tid}-shortlist-toggle`}
                          aria-pressed={!!c.shortlisted}
                          title={c.shortlisted
                            ? t('recruitment.candidates.shortlist.removeCta', { defaultValue: 'Remove from shortlist' })
                            : t('recruitment.candidates.shortlist.addCta', { defaultValue: 'Add to shortlist' })}
                        >
                          {shortlistBusy === c.candidate_user_id
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Star className={`h-4 w-4 ${c.shortlisted ? 'fill-amber-500' : ''}`} />}
                        </button>
                      ) : null}
                    </TableCell>
                  )}
                  {!isClient && (
                    <TableCell className="w-9 px-2">
                      {!isPoolRow && (
                      <Checkbox
                        checked={selectedAids.includes(c.assignment_id)}
                        onCheckedChange={(v) => {
                          setSelectedAids((prev) => {
                            const next = v
                              ? [...prev, c.assignment_id]
                              : prev.filter((x) => x !== c.assignment_id);
                            // Cap at 4 — silently drop the oldest if user
                            // tries to pick a 5th instead of disabling rows.
                            return next.slice(-4);
                          });
                        }}
                        data-testid={`${tid}-select-checkbox`}
                        aria-label={t('recruitment.candidates.compare.cta', { defaultValue: 'Compare' })}
                      />
                      )}
                    </TableCell>
                  )}
                  {!isClient && (
                    <TableCell className="w-9 px-2">
                      {!isPoolRow && (
                      <button
                        type="button"
                        onClick={() => toggleShortlist(c)}
                        disabled={shortlistBusy === c.candidate_user_id}
                        className={`p-1 rounded-md transition-colors ${
                          c.shortlisted
                            ? 'text-amber-500 hover:bg-amber-50'
                            : 'text-muted-foreground hover:text-amber-500 hover:bg-amber-50'
                        }`}
                        data-testid={`${tid}-shortlist-toggle`}
                        aria-pressed={!!c.shortlisted}
                        title={
                          c.shortlisted
                            ? t('recruitment.candidates.shortlist.removeCta', { defaultValue: 'Remove from shortlist' })
                            : t('recruitment.candidates.shortlist.addCta', { defaultValue: 'Add to shortlist' })
                        }
                      >
                        {shortlistBusy === c.candidate_user_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Star
                            className={`h-4 w-4 ${c.shortlisted ? 'fill-amber-500' : ''}`}
                          />
                        )}
                      </button>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="font-medium">
                    {c.candidate_name}
                    {(isClient || companyId) && (
                    <button type="button" title={L('Documentos del candidato', 'Candidate documents')}
                            onClick={() => setDocsRow(c)}
                            className="ml-1.5 inline-flex align-middle text-teal-600 hover:text-teal-800"
                            data-testid={`${tid}-docs-btn`}>
                      <FolderOpen className="h-4 w-4" />
                    </button>
                    )}
                    {isClient && c.pool_candidate_id && (
                    <button type="button" title={L('Invitar a entregar CV (enlace seguro)', 'Invite to submit CV (secure link)')}
                            onClick={() => inviteCandidate(c)}
                            className="ml-1 inline-flex align-middle text-sky-600 hover:text-sky-800"
                            data-testid={`${tid}-invite-btn`}>
                      <Send className="h-3.5 w-3.5" />
                    </button>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                    {c.position || '—'}
                  </TableCell>
                  <TableCell className="text-sm max-w-[220px]">
                    <span className="block truncate" title={c.assessment_title || ''}
                          data-testid={`${tid}-assessment-title`}>
                      {c.assessment_title || '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {t(
                        `recruitment.candidates.assessmentStatus.${c.assessment_status}`,
                        { defaultValue: c.assessment_status },
                      )}
                    </Badge>
                    {c.final_status === 'hired' && (
                      <Badge className="text-[10px] ml-1 bg-emerald-600 text-white border-0"
                             data-testid={`${tid}-hired-badge`}>
                        {L('Contratado/a', 'Hired')}
                      </Badge>
                    )}
                    {c.final_status === 'pending_activation' && (
                      <Badge variant="outline" className="text-[10px] ml-1 bg-amber-50 text-amber-700 border-amber-300"
                             data-testid={`${tid}-pending-activation-badge`}>
                        {L('Incorporación pendiente', 'Activation pending')}
                      </Badge>
                    )}
                    {c.final_status === 'not_selected' && (
                      <Badge variant="outline" className="text-[10px] ml-1 bg-slate-100 text-slate-500"
                             data-testid={`${tid}-notselected-badge`}>
                        {L('No seleccionado/a', 'Not selected')}
                      </Badge>
                    )}
                    {(c.assessment_progress || 0) > 0 && (c.assessment_status !== 'submitted') && (
                      <span className="text-xs text-muted-foreground ml-1 tabular-nums">
                        {c.assessment_progress}%
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <ReportStatusBadge status={c.report_status} t={t} isClient={isClient} />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <ScoreCell
                      value={c.overall_score}
                      band={(c.score_band || c.fit_band) ? t(`reports.bands.${String(c.score_band || c.fit_band).toLowerCase()}`, { defaultValue: c.score_band || c.fit_band }) : null}
                      hidden={isClient && c.report_id != null && !c.report_released && !c.results_available}
                      hiddenLabel={t('recruitment.candidates.scoreHidden')}
                    />
                  </TableCell>
                  <TableCell className="hidden xl:table-cell text-xs text-muted-foreground">
                    {fmtDate(c.last_activity_at, i18n.language)}
                  </TableCell>
                  {!isClient && (
                    <TableCell>
                      {isPoolRow ? <span className="text-xs text-muted-foreground">—</span> : (
                      <DecisionPill
                        decision={c.decision}
                        busy={decisionBusy === c.candidate_user_id}
                        onSet={(status, note) => setDecision(c.candidate_user_id, status, note)}
                        t={t}
                        rowTestId={tid}
                      />
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    {isClient ? (
                      isPoolRow ? (
                        canManage ? (
                          <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-destructive"
                                  onClick={() => removePoolCandidate(c)}
                                  disabled={removeBusy === c.pool_candidate_id}
                                  data-testid={`${tid}-remove-pool`}
                                  title={t('recruitment.candidates.removePool', { defaultValue: L('Retirar candidato', 'Remove candidate') })}>
                            {removeBusy === c.pool_candidate_id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        ) : <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <div className="flex items-center justify-end gap-1.5">
                          {canManage && !c.process_final && c.candidate_user_id
                            && addAssessMeta.firstAid[c.candidate_user_id] === c.assignment_id && (
                            <Button size="sm" variant="outline" className="gap-1.5"
                                    onClick={() => { setAddAssessFor(c); setAddAssessSel([]); }}
                                    data-testid={`${tid}-add-assessment`}
                                    title={L('Agregar otra evaluación a este candidato', 'Add another assessment to this candidate')}>
                              <Plus className="h-3.5 w-3.5" />
                              {L('Agregar evaluación', 'Add assessment')}
                            </Button>
                          )}
                          {/* CTH-REC-075 — explicit per-candidate decision on
                              closed 'selected' processes before activation. */}
                          {(canManage || canActivate) && isClient && c.final_status === 'pending_activation' && (
                            <>
                              <Button size="sm" variant="outline"
                                      className="gap-1.5 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                                      onClick={() => clientDecision(c, 'hired')}
                                      data-testid={`${tid}-decide-hire`}>
                                <UserPlus className="h-3.5 w-3.5" />
                                {L('Confirmar contratación', 'Confirm hire')}
                              </Button>
                              <Button size="sm" variant="outline"
                                      className="gap-1.5 border-rose-300 text-rose-600 hover:bg-rose-50"
                                      onClick={() => clientDecision(c, 'not_selected')}
                                      data-testid={`${tid}-decide-reject`}>
                                {L('No contratado', 'Not selected')}
                              </Button>
                            </>
                          )}
                          {(canManage || canActivate) && ((c.shortlisted && c.final_status == null) || (c.final_status === 'hired' && !c.hired_activated)) && (
                            <Button size="sm" variant="outline"
                                    className="gap-1.5 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                                    onClick={() => setConvertFor(c)}
                                    data-testid={`${tid}-convert-collaborator`}
                                    title={L('Activar al candidato seleccionado como colaborador de la organización', 'Activate the selected candidate as an employee of the organization')}>
                              <UserPlus className="h-3.5 w-3.5" />
                              {L('Activar como colaborador', 'Activate as Employee')}
                            </Button>
                          )}
                          {canManage && c.candidate_email && (
                            <Button size="sm" variant="outline" onClick={() => openAccess(c)}
                                    className="gap-1.5"
                                    data-testid={`${tid}-candidate-access`}>
                              <KeyRound className="h-3.5 w-3.5" />
                              {t('recruitment.candidates.manageAccess', { defaultValue: L('Gestionar acceso', 'Manage access') })}
                            </Button>
                          )}
                          {c.results_available && (
                            <Button size="sm" variant="outline" className="gap-1.5 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                                    onClick={() => openResults(c)}
                                    data-testid={`${tid}-view-results`}>
                              <BarChart3 className="h-3.5 w-3.5" />
                              {t('recruitment.candidates.viewResults', { defaultValue: L('Ver resultados', 'View results') })}
                            </Button>
                          )}
                          {canOpenReport ? (
                        <Link to={reportHref(c.report_id)}>
                          <Button size="sm" variant="outline" className="gap-1.5"
                                  data-testid={`${tid}-open-report`}>
                            <FileText className="h-3.5 w-3.5" />
                            {t('recruitment.candidates.openReport')}
                          </Button>
                        </Link>
                      ) : c.results_available ? null : (c.action_state === 'no_action'
                          && !['pending_activation', 'hired'].includes(c.final_status)) ? (
                        <span className="text-xs text-muted-foreground" data-testid={`${tid}-no-action`}>
                          {t('recruitment.candidates.noActions', { defaultValue: 'Sin acciones' })}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground" data-testid={`${tid}-report-pending`}>
                          {t('recruitment.candidates.reportPending')}
                        </span>
                      )}
                        </div>
                      )
                    ) : isPoolRow ? (
                      <span className="text-xs text-muted-foreground">
                        {t('recruitment.candidates.assessmentStatus.not_assigned', { defaultValue: 'Not assigned yet' })}
                      </span>
                    ) : (
                      <RosterRowActions
                        row={c}
                        reportHref={reportHref}
                        onAction={runAction}
                        onOpenAccess={openAccess}
                        runningKey={runningKey}
                        t={t}
                        rowTestId={tid}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Dialog open={!!docsRow} onOpenChange={(o) => !o && setDocsRow(null)}>
          <DialogContent className="sm:max-w-xl" data-testid="candidate-docs-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">
                {L('Documentos — ', 'Documents — ')}{docsRow?.candidate_name}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {L('Documentos administrativos del candidato', 'Candidate administrative documents')}
              </DialogDescription>
            </DialogHeader>
            {docsRow && (
              <DocumentPanel suite="talentcheck" entityType="candidate"
                             entityId={docsRow.assignment_id || docsRow.pool_candidate_id}
                             employeeUserId={docsRow.candidate_user_id}
                             defaultDocType="cv"
                             docTypes={["cv", "application", "diploma", "certification",
                                        "training_certificate", "credential", "other"]}
                             mode={isClient ? 'client' : 'admin'}
                             companyId={isClient ? null : companyId}
                             readOnly={!isClient}
                             compact />
            )}
          </DialogContent>
        </Dialog>
        </>
      )}

      {/* Sprint TC-A.2 — release confirmation (mandatory human-review ack). */}
      <AlertDialog
        open={!!releaseConfirm}
        onOpenChange={(o) => { if (!o) setReleaseConfirm(null); }}
      >
        <AlertDialogContent data-testid="release-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('recruitment.candidates.releaseConfirm.title', {
                defaultValue: 'Release report to client?',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('recruitment.candidates.releaseConfirm.body', {
                defaultValue:
                  'The candidate "{{name}}"\'s report will become visible to the client. '
                  + 'By releasing, you confirm a human reviewer has validated the content.',
                name: releaseConfirm?.candidate_name || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
            <ShieldCheck className="h-4 w-4 text-amber-700 mt-0.5" />
            <span className="text-amber-900">
              {t('recruitment.candidates.releaseConfirm.humanReview', {
                defaultValue: 'Human review is mandatory before release.',
              })}
            </span>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="release-cancel">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRelease}
              disabled={!!runningKey}
              data-testid="release-confirm"
            >
              {runningKey ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              {t('recruitment.candidates.releaseConfirm.confirmCta', { defaultValue: 'Confirm & release' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Split model — automatic quantitative results viewer (client). */}
      {isClient && (
        <Dialog open={!!resultsRow} onOpenChange={(o) => { if (!o) { setResultsRow(null); setResultsData(null); } }}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tc-results-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                {L('Resultados automáticos', 'Automatic results')}
              </DialogTitle>
              <DialogDescription>
                {L('Resultados cuantitativos del instrumento aplicado. El informe interpretativo profesional de CTH es un entregable separado.',
                   'Quantitative results of the applied instrument. The professional CTH interpretive report is a separate deliverable.')}
              </DialogDescription>
            </DialogHeader>
            {resultsLoading ? (
              <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : resultsData ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-md border border-border bg-muted/20 px-4 py-3">
                  <div>
                    <p className="font-semibold" data-testid="tc-results-candidate">{resultsData.candidate_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(isES && resultsData.questionnaire_name_es) || resultsData.questionnaire_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold tabular-nums" data-testid="tc-results-score">{resultsData.overall_score}</p>
                    {resultsData.band && (
                      <Badge variant="outline" className="text-xs">
                        {t(`reports.bands.${String(resultsData.band).toLowerCase()}`, { defaultValue: resultsData.band })}
                      </Badge>
                    )}
                  </div>
                </div>
                {(resultsData.sections || []).length > 0 && (
                  <div className="space-y-2" data-testid="tc-results-sections">
                    {resultsData.sections.map((s, i) => (
                      <div key={`${s.name}-${i}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span>{s.name}</span>
                          <span className="tabular-nums font-medium">{s.score}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full bg-primary" style={{ width: `${Math.min(100, Math.max(0, s.score))}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!resultsData.report_released && (
                  <p className="text-xs text-muted-foreground">
                    {L('Informe interpretativo — con control de calidad CTH (entrega separada).',
                       'Interpretive report — subject to CTH quality review (delivered separately).')}
                  </p>
                )}
                <DirectResultsDisclaimer />
                {/* Founder-approved — automatic-results export via the existing
                    CTH branded renderer (quantitative only + disclaimer). */}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" className="gap-1.5" disabled={!!resultsDlBusy}
                          onClick={() => downloadResults('pdf')} data-testid="tc-results-download-pdf">
                    {resultsDlBusy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {L('Descargar PDF', 'Download PDF')}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" disabled={!!resultsDlBusy}
                          onClick={() => downloadResults('docx')} data-testid="tc-results-download-docx">
                    {resultsDlBusy === 'docx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {L('Descargar Word', 'Download Word')}
                  </Button>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      )}

      {/* Candidate access dialog — existing mechanism, now client-visible too. */}
      {(!isClient || canManage) && (
        <Dialog open={!!accessRow} onOpenChange={(o) => { if (!o) { setAccessRow(null); setAccessResult(null); } }}>
          <DialogContent data-testid="candidate-access-dialog">
            <DialogHeader>
              <DialogTitle>{t('recruitment.candidates.access.title')}</DialogTitle>
              <DialogDescription>{t('recruitment.candidates.access.hint')}</DialogDescription>
            </DialogHeader>
            {!accessInfo ? (
              <div className="py-6 flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('common.loading')}
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
                  <span className="text-muted-foreground">{t('recruitment.candidates.thCandidate')}</span>
                  <span className="font-medium" data-testid="access-candidate-name">{accessInfo.candidate_name || '—'}</span>
                  <span className="text-muted-foreground">{t('recruitment.candidates.access.email')}</span>
                  <span className="font-medium" data-testid="access-candidate-email">{accessInfo.candidate_email || '—'}</span>
                  {/* Founder UAT 2026-06 — identify WHICH assignment is managed. */}
                  <span className="text-muted-foreground">{t('recruitment.candidates.thAssessment')}</span>
                  <span className="font-medium" data-testid="access-assessment-title">
                    {accessRow?.assessment_title || accessInfo.assessment_title || '—'}
                  </span>
                  <span className="text-muted-foreground">{t('recruitment.candidates.access.linked')}</span>
                  <span data-testid="access-linked-state">
                    {accessInfo.account_user_id
                      ? <Badge variant="outline" className="text-xs">{accessInfo.account_email}</Badge>
                      : <span className="text-muted-foreground">{t('recruitment.candidates.access.notLinked')}</span>}
                  </span>
                  <span className="text-muted-foreground">{t('common.status', { defaultValue: 'Estado' })}</span>
                  <span data-testid="access-account-status">
                    <Badge variant="outline" className="text-xs">
                      {t(`recruitment.candidates.access.status.${(accessResult ? 'pending' : accessInfo.account_status) || 'none'}`)}
                    </Badge>
                  </span>
                </div>
                {accessResult?.temp_password ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="access-temp-password-box">
                    <p className="text-xs font-semibold text-amber-900">
                      {t('recruitment.candidates.access.tempPassword')}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-white border rounded px-2 py-1" data-testid="access-temp-password">
                        {accessResult.temp_password}
                      </code>
                      <Button
                        size="sm" variant="outline" className="gap-1.5"
                        data-testid="access-copy-password"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(accessResult.temp_password);
                            toast.success(t('recruitment.candidates.access.copied'));
                          } catch { toast.message(accessResult.temp_password); }
                        }}
                      >
                        <ClipboardCopy className="h-3.5 w-3.5" />
                        {t('recruitment.candidates.access.copy')}
                      </Button>
                    </div>
                    <p className="text-xs text-amber-900/80">
                      {t('recruitment.candidates.access.portalHint')}
                    </p>
                  </div>
                ) : (
                  <Button
                    onClick={activateAccess}
                    disabled={accessBusy || !accessInfo.candidate_email}
                    className="gap-1.5 w-full"
                    data-testid="access-activate-btn"
                  >
                    {accessBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                    {accessInfo.account_status === 'none'
                      ? t('recruitment.candidates.access.activate')
                      : t('recruitment.candidates.access.reset')}
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Founder 2026-06 P0 — add additional assessments to an existing candidate. */}
      {isClient && canManage && (
        <Dialog open={!!addAssessFor} onOpenChange={(o) => { if (!o) { setAddAssessFor(null); setAddAssessSel([]); } }}>
          <DialogContent className="sm:max-w-lg" data-testid="tc-add-assessment-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                {L('Agregar evaluación', 'Add assessment')}
              </DialogTitle>
              <DialogDescription>
                {L(
                  `Asigna evaluaciones adicionales a ${addAssessFor?.candidate_name || 'el candidato'} dentro de este mismo proceso. Se usa la misma cuenta de acceso; las evaluaciones ya completadas no se modifican.`,
                  `Assign additional assessments to ${addAssessFor?.candidate_name || 'the candidate'} within this same process. The same access account is used; completed assessments are not modified.`,
                )}
              </DialogDescription>
            </DialogHeader>
            {addAssessEligible.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-3" data-testid="tc-add-assessment-empty">
                {L('No hay más evaluaciones elegibles disponibles: todas las evaluaciones autorizadas para TalentCheck ya fueron asignadas a este candidato.',
                   'No further eligible assessments available: every assessment authorized for TalentCheck is already assigned to this candidate.')}
              </p>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-1.5 py-1" data-testid="tc-add-assessment-list">
                {addAssessEligible.map((x) => (
                  <label key={x.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40">
                    <Checkbox
                      checked={addAssessSel.includes(x.id)}
                      onCheckedChange={(v) => setAddAssessSel((s) => v ? [...s, x.id] : s.filter((i) => i !== x.id))}
                      data-testid={`tc-add-assessment-q-${x.id}`}
                    />
                    <span className="truncate">{x.name}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setAddAssessFor(null); setAddAssessSel([]); }}
                      data-testid="tc-add-assessment-cancel">
                {t('common.cancel', { defaultValue: L('Cancelar', 'Cancel') })}
              </Button>
              <Button onClick={submitAddAssessments} disabled={addAssessBusy || addAssessSel.length === 0}
                      className="gap-1.5" data-testid="tc-add-assessment-confirm">
                {addAssessBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {L('Asignar', 'Assign')} {addAssessSel.length > 0 ? `(${addAssessSel.length})` : ''}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Sprint TC-C.1 — side-by-side comparison sheet (admin only). */}
      {!isClient && (
        <CandidateComparisonSheet
          open={compareOpen}
          onOpenChange={setCompareOpen}
          processId={processId}
          selectedAids={selectedAids}
        />
      )}
      {/* Founder UAT 2026-06 — hired candidate → collaborator → occupant */}
      <ConvertToCollaboratorDialog
        open={!!convertFor}
        onClose={() => setConvertFor(null)}
        processId={processId}
        candidate={convertFor}
        onConverted={() => { load(); onProcessChanged && onProcessChanged(); }}
      />
    </Card>
  );
}

/**
 * Per-row overflow menu. Pure renderer — receives the row + the actions
 * controller from the parent. Only mounted for variant="admin".
 */
function RosterRowActions({ row, reportHref, onAction, onOpenAccess, runningKey, t, rowTestId }) {
  const actions = row.actions_available || [];
  const busy = (act) => runningKey === `${row.assignment_id}:${act}`;
  return (
    <div className="flex items-center justify-end gap-1.5">
      {row.report_id && actions.includes('open_report') && (
        <Link to={reportHref(row.report_id)}>
          <Button size="sm" variant="outline" className="gap-1.5" data-testid={`${rowTestId}-open-report`}>
            <FileText className="h-3.5 w-3.5" />
            {t('recruitment.candidates.openReport')}
          </Button>
        </Link>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" data-testid={`${rowTestId}-actions-menu`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs uppercase text-muted-foreground">
            {t('recruitment.candidates.actions.title', { defaultValue: 'Candidate actions' })}
          </DropdownMenuLabel>
          {actions.includes('open_assignment') && (
            <DropdownMenuItem asChild data-testid={`${rowTestId}-open-assignment`}>
              <Link to={`/admin/evaluations/${row.assignment_id}`}>
                <Eye className="h-4 w-4 mr-2" />
                {t('recruitment.candidates.actions.openAssignment', { defaultValue: 'View assignment' })}
              </Link>
            </DropdownMenuItem>
          )}
          {actions.includes('open_report') && row.report_id && (
            <DropdownMenuItem asChild data-testid={`${rowTestId}-open-report-menu`}>
              <Link to={reportHref(row.report_id)}>
                <FileText className="h-4 w-4 mr-2" />
                {t('recruitment.candidates.actions.openReport', { defaultValue: 'View report' })}
              </Link>
            </DropdownMenuItem>
          )}
          {actions.includes('copy_invite_link') && (
            <DropdownMenuItem
              onSelect={() => onAction(row, 'copy_invite_link')}
              data-testid={`${rowTestId}-copy-link`}
            >
              <ClipboardCopy className="h-4 w-4 mr-2" />
              {t('recruitment.candidates.actions.copyInviteLink', { defaultValue: 'Copy invite link' })}
            </DropdownMenuItem>
          )}
          {row.candidate_email && onOpenAccess && (
            <DropdownMenuItem
              onSelect={() => onOpenAccess(row)}
              data-testid={`${rowTestId}-candidate-access`}
            >
              <KeyRound className="h-4 w-4 mr-2" />
              {t('recruitment.candidates.access.cta')}
            </DropdownMenuItem>
          )}
          {(actions.includes('start_review') || actions.includes('approve_report') || actions.includes('release_report')) && (
            <DropdownMenuSeparator />
          )}
          {actions.includes('start_review') && (
            <DropdownMenuItem
              onSelect={() => onAction(row, 'start_review')}
              disabled={busy('start_review')}
              data-testid={`${rowTestId}-start-review`}
            >
              {busy('start_review') ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              {t('recruitment.candidates.actions.startReview', { defaultValue: 'Start review' })}
            </DropdownMenuItem>
          )}
          {actions.includes('approve_report') && (
            <DropdownMenuItem
              onSelect={() => onAction(row, 'approve_report')}
              disabled={busy('approve_report')}
              data-testid={`${rowTestId}-approve-report`}
            >
              {busy('approve_report') ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {t('recruitment.candidates.actions.approveReport', { defaultValue: 'Approve report' })}
            </DropdownMenuItem>
          )}
          {actions.includes('release_report') && (
            <DropdownMenuItem
              onSelect={() => onAction(row, 'release_report')}
              disabled={busy('release_report')}
              className="text-success focus:text-success"
              data-testid={`${rowTestId}-release-report`}
            >
              <Send className="h-4 w-4 mr-2" />
              {t('recruitment.candidates.actions.releaseReport', { defaultValue: 'Release to client' })}
            </DropdownMenuItem>
          )}
          {actions.length === 1 && actions[0] === 'open_assignment' && (
            <DropdownMenuItem disabled className="text-xs text-muted-foreground">
              <ExternalLink className="h-3 w-3 mr-2" />
              {t('recruitment.candidates.actions.noFurtherActions', { defaultValue: 'No further actions yet' })}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Sprint TC-A.3 — Decision statuses + sub-component.
const DECISION_STATUSES = ['shortlisted', 'continue_process', 'on_hold', 'rejected', 'hired'];

const DECISION_TONE = {
  shortlisted: 'bg-blue-100 text-blue-700 border-blue-200',
  continue_process: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  on_hold: 'bg-amber-100 text-amber-700 border-amber-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  hired: 'bg-green-100 text-green-700 border-green-200',
};

function DecisionPill({ decision, busy, onSet, t, rowTestId }) {
  const [open, setOpen] = React.useState(false);
  const [picked, setPicked] = React.useState(decision?.status || '');
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setPicked(decision?.status || '');
      setNote('');
    }
  }, [open, decision]);

  const currentLabel = decision?.status
    ? t(`recruitment.candidates.decision.${decision.status}`, {
        defaultValue: decision.status.replace(/_/g, ' '),
      })
    : t('recruitment.candidates.decision.notSet', { defaultValue: 'Not set' });

  const tone = decision?.status
    ? DECISION_TONE[decision.status]
    : 'bg-muted text-muted-foreground border-muted';

  const submit = async () => {
    if (!picked) return;
    await onSet(picked, note);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium ${tone}`}
          disabled={busy}
          data-testid={`${rowTestId}-decision-trigger`}
        >
          {busy && <Loader2 className="h-3 w-3 animate-spin" />}
          {!busy && <span className="truncate max-w-[120px]">{currentLabel}</span>}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64" data-testid={`${rowTestId}-decision-popover`}>
        <p className="text-xs font-semibold mb-1.5">
          {t('recruitment.candidates.decision.setTitle', { defaultValue: 'CTH assessment recommendation' })}
        </p>
        <p className="text-xs text-muted-foreground mb-1.5">
          {t('recruitment.candidates.decision.scopeHint', { defaultValue: "Internal CTH recommendation per candidate. The client's final hiring decision is recorded separately by the Client Admin at stage 10." })}
        </p>
        <div className="grid grid-cols-1 gap-1.5 mb-3">
          {DECISION_STATUSES.map((s) => {
            const isPicked = picked === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setPicked(s)}
                className={`flex items-center justify-between rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${
                  isPicked
                    ? `${DECISION_TONE[s]} ring-2 ring-primary/30 ring-offset-1`
                    : 'bg-background hover:bg-muted/40 border-input text-foreground'
                }`}
                data-testid={`${rowTestId}-decision-${s}`}
              >
                <span>
                  {t(`recruitment.candidates.decision.${s}`, {
                    defaultValue: s.replace(/_/g, ' '),
                  })}
                </span>
                {isPicked && <CheckCircle2 className="h-3 w-3" />}
              </button>
            );
          })}
        </div>
        <Textarea
          rows={2}
          className="text-xs"
          placeholder={t('recruitment.candidates.decision.notePlaceholder', {
            defaultValue: 'Optional note (e.g. reason, next steps)…',
          })}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid={`${rowTestId}-decision-note`}
        />
        <div className="flex justify-end gap-2 mt-2">
          <Button
            size="sm" variant="ghost"
            onClick={() => setOpen(false)}
            data-testid={`${rowTestId}-decision-cancel`}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={!picked || busy}
            data-testid={`${rowTestId}-decision-save`}
          >
            {busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            {t('common.save', { defaultValue: 'Save' })}
          </Button>
        </div>
        {decision?.history?.length > 0 && (
          <div className="border-t mt-2 pt-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              {t('recruitment.candidates.decision.previous', { defaultValue: 'Previous decisions' })}
            </p>
            <ul className="text-xs text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto">
              {decision.history.slice().reverse().map((h, i) => (
                <li key={i}>
                  <span className="capitalize">{(h.status || '').replace(/_/g, ' ')}</span>
                  <span className="text-xs"> · {(h.set_at || '').slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Keeps existing default export contract unchanged.
export default CandidateRosterCard;
